import { useState, useMemo } from 'react';
import { X } from '@phosphor-icons/react';

// Common medications database for searching
const MEDICATIONS_DATABASE = [
  { id: 'med1', name: 'Amoxicillin', strength: '500mg', form: 'Capsule', category: 'Antibiotic' },
  { id: 'med2', name: 'Amoxicillin', strength: '250mg', form: 'Capsule', category: 'Antibiotic' },
  { id: 'med3', name: 'Lisinopril', strength: '10mg', form: 'Tablet', category: 'ACE Inhibitor' },
  { id: 'med4', name: 'Lisinopril', strength: '20mg', form: 'Tablet', category: 'ACE Inhibitor' },
  { id: 'med5', name: 'Metformin', strength: '500mg', form: 'Tablet', category: 'Diabetes' },
  { id: 'med6', name: 'Metformin', strength: '1000mg', form: 'Tablet', category: 'Diabetes' },
  { id: 'med7', name: 'Atorvastatin', strength: '10mg', form: 'Tablet', category: 'Statin' },
  { id: 'med8', name: 'Atorvastatin', strength: '20mg', form: 'Tablet', category: 'Statin' },
  { id: 'med9', name: 'Omeprazole', strength: '20mg', form: 'Capsule', category: 'PPI' },
  { id: 'med10', name: 'Prednisone', strength: '10mg', form: 'Tablet', category: 'Corticosteroid' },
  { id: 'med11', name: 'Azithromycin', strength: '250mg', form: 'Tablet', category: 'Antibiotic' },
  { id: 'med12', name: 'Ibuprofen', strength: '400mg', form: 'Tablet', category: 'NSAID' },
  { id: 'med13', name: 'Ibuprofen', strength: '800mg', form: 'Tablet', category: 'NSAID' },
  { id: 'med14', name: 'Gabapentin', strength: '300mg', form: 'Capsule', category: 'Anticonvulsant' },
  { id: 'med15', name: 'Hydrochlorothiazide', strength: '25mg', form: 'Tablet', category: 'Diuretic' },
];

export default function PrescriptionModal({ 
  isOpen, 
  onClose, 
  onAddPrescription 
}) {
  const [selectedMed, setSelectedMed] = useState(null);
  const [medSearch, setMedSearch] = useState('');
  const [rxDetails, setRxDetails] = useState({
    dosage: '',
    frequency: '',
    duration: '',
    quantity: '',
    refills: 0,
    instructions: '',
    strength: '',
    form: ''
  });

  // Search medications
  const medResults = useMemo(() => {
    if (!medSearch.trim()) return [];
    const query = medSearch.toLowerCase();
    return MEDICATIONS_DATABASE.filter(
      med => med.name.toLowerCase().includes(query) || 
             med.category.toLowerCase().includes(query)
    ).slice(0, 10);
  }, [medSearch]);

  const handleAddPrescription = () => {
    if (!selectedMed && !medSearch.trim()) {
      return;
    }

    // Build medication object
    let medication;
    if (selectedMed) {
      medication = selectedMed;
    } else {
      // Custom medication from search text
      medication = {
        id: `custom-${Date.now()}`,
        name: medSearch.trim(),
        strength: rxDetails.strength || '',
        form: rxDetails.form || 'Tablet'
      };
    }

    const newPrescription = {
      id: `rx-${Date.now()}`,
      medication,
      ...rxDetails
    };

    onAddPrescription(newPrescription);
    
    // Reset form
    setSelectedMed(null);
    setMedSearch('');
    setRxDetails({
      dosage: '',
      frequency: '',
      duration: '',
      quantity: '',
      refills: 0,
      instructions: '',
      strength: '',
      form: ''
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 m-4" data-testid="rx-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">Add Prescription</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={24} />
          </button>
        </div>
        
        {/* Medication Search */}
        <div className="mb-4">
          <label className="label-clinical">Medication</label>
          {selectedMed ? (
            <div className="flex items-center justify-between p-3 bg-teal-50 rounded-lg border border-teal-200">
              <span>{selectedMed.name} {selectedMed.strength} ({selectedMed.form})</span>
              <button onClick={() => setSelectedMed(null)} className="text-red-500">
                <X size={18} />
              </button>
            </div>
          ) : (
            <div className="relative">
              <input
                type="text"
                value={medSearch}
                onChange={(e) => setMedSearch(e.target.value)}
                placeholder="Type medication name or search..."
                className="input-clinical w-full"
                data-testid="med-search"
              />
              <p className="text-xs text-slate-500 mt-1">
                Select from suggestions below or type any medication name
              </p>
              {medResults.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {medResults.map((med) => (
                    <button
                      key={med.id}
                      onClick={() => {
                        setSelectedMed(med);
                        setMedSearch('');
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0"
                    >
                      <p className="font-medium text-sm">{med.name} {med.strength}</p>
                      <p className="text-xs text-slate-500">{med.form} - {med.category}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Strength/Form for custom medications */}
        {!selectedMed && medSearch.trim() && (
          <div className="grid grid-cols-2 gap-4 mb-4 p-3 bg-slate-50 rounded-lg">
            <div>
              <label className="label-clinical">Strength</label>
              <input
                type="text"
                value={rxDetails.strength || ''}
                onChange={(e) => setRxDetails({...rxDetails, strength: e.target.value})}
                placeholder="e.g., 500mg"
                className="input-clinical"
              />
            </div>
            <div>
              <label className="label-clinical">Form</label>
              <select
                value={rxDetails.form || ''}
                onChange={(e) => setRxDetails({...rxDetails, form: e.target.value})}
                className="input-clinical"
              >
                <option value="">Select form...</option>
                <option value="Tablet">Tablet</option>
                <option value="Capsule">Capsule</option>
                <option value="Liquid">Liquid/Solution</option>
                <option value="Cream">Cream/Ointment</option>
                <option value="Injection">Injection</option>
                <option value="Inhaler">Inhaler</option>
                <option value="Patch">Patch</option>
                <option value="Drops">Drops</option>
                <option value="Suppository">Suppository</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>
        )}
        
        {/* Rx Details */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="label-clinical">Dosage</label>
            <input
              type="text"
              value={rxDetails.dosage}
              onChange={(e) => setRxDetails({...rxDetails, dosage: e.target.value})}
              placeholder="e.g., 1 tablet"
              className="input-clinical"
            />
          </div>
          <div>
            <label className="label-clinical">Frequency</label>
            <select
              value={rxDetails.frequency}
              onChange={(e) => setRxDetails({...rxDetails, frequency: e.target.value})}
              className="input-clinical"
            >
              <option value="">Select...</option>
              <option value="Once daily">Once daily</option>
              <option value="Twice daily">Twice daily</option>
              <option value="Three times daily">Three times daily</option>
              <option value="Four times daily">Four times daily</option>
              <option value="Every 4 hours">Every 4 hours</option>
              <option value="Every 6 hours">Every 6 hours</option>
              <option value="Every 8 hours">Every 8 hours</option>
              <option value="Every 12 hours">Every 12 hours</option>
              <option value="As needed">As needed (PRN)</option>
              <option value="At bedtime">At bedtime</option>
              <option value="With meals">With meals</option>
            </select>
          </div>
          <div>
            <label className="label-clinical">Duration</label>
            <input
              type="text"
              value={rxDetails.duration}
              onChange={(e) => setRxDetails({...rxDetails, duration: e.target.value})}
              placeholder="e.g., 10 days"
              className="input-clinical"
            />
          </div>
          <div>
            <label className="label-clinical">Quantity</label>
            <input
              type="text"
              value={rxDetails.quantity}
              onChange={(e) => setRxDetails({...rxDetails, quantity: e.target.value})}
              placeholder="e.g., 30"
              className="input-clinical"
            />
          </div>
          <div>
            <label className="label-clinical">Refills</label>
            <input
              type="number"
              min="0"
              max="12"
              value={rxDetails.refills}
              onChange={(e) => setRxDetails({...rxDetails, refills: parseInt(e.target.value) || 0})}
              className="input-clinical"
            />
          </div>
        </div>
        
        <div className="mb-4">
          <label className="label-clinical">Patient Instructions</label>
          <textarea
            value={rxDetails.instructions}
            onChange={(e) => setRxDetails({...rxDetails, instructions: e.target.value})}
            placeholder="Special instructions for the patient..."
            className="input-clinical min-h-[80px]"
          />
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 btn-outline"
          >
            Cancel
          </button>
          <button
            onClick={handleAddPrescription}
            className="flex-1 btn-teal"
            data-testid="confirm-add-rx"
          >
            Add Prescription
          </button>
        </div>
      </div>
    </div>
  );
}
